import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { DocumentPageSchema, HtmlExtractionArm } from '@avisoma/shared';
import StatusIndicator from '../../components/StatusIndicator';
import { ActionableButton } from '../../components/ActionableButton';
import AuthGuard from '../../components/AuthGuard';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import backend from '../../services/backend';
import { HtmlConversionDialog } from './HtmlConversionDialog';

type HtmlFallbackReason = NonNullable<
  NonNullable<NonNullable<DocumentPageSchema['processing']>['htmlConversion']>['fallbackReason']
>;

type FallbackLabel = { badge: string; title: string };

type ArmDisplay = { text: string; degraded: boolean; title: string };

// The label a browser running an older bundle than the server falls back to. A
// reason it has never heard of still means "extraction read the PDF", so it must
// never land on the green HTML-was-used branch.
const UNKNOWN_FALLBACK: FallbackLabel = {
  badge: 'PDF used',
  title: 'Extracted from PDF, not the converted HTML',
};

// Why extraction read the raw PDF even though a conversion existed. Keyed off the
// stamp's `fallbackReason` so a reason added server-side fails to compile here
// until it has a label of its own — the runtime lookup below is for a stale
// bundle, not for a reason we forgot to label.
const FALLBACK_LABEL: Record<HtmlFallbackReason, FallbackLabel> = {
  'category-policy': {
    badge: 'PDF used by policy',
    title: 'PDF selected by category policy — extracted from PDF, not the converted HTML',
  },
  'fabricated-header': {
    badge: 'PDF used — fabricated header row',
    title:
      'The conversion invented a column-header row this page does not print — extracted from PDF, not the converted HTML',
  },
  'column-shift': {
    badge: 'PDF used — column shift',
    title: 'A table row of the conversion sits under the wrong column — extracted from PDF, not the converted HTML',
  },
  'row-shift': {
    badge: 'PDF used — row shift',
    title: 'A table value of the conversion sits on the wrong row — extracted from PDF, not the converted HTML',
  },
  'column-collapse': {
    badge: 'PDF used — column collapse',
    title:
      'Table rows of the conversion glue several printed columns into one cell — extracted from PDF, not the converted HTML',
  },
  'token-loss': {
    badge: 'PDF used — token loss',
    title: 'The conversion lost a large share of the page values — extracted from PDF, not the converted HTML',
  },
  'ungrounded-strike': {
    badge: 'PDF used — ungrounded strike',
    title:
      'The conversion marked rows as struck through, but the PDF has no drawn-mark evidence (no raster, no annotation, no line through text) — extracted from PDF, not the converted HTML',
  },
};

const ARM_LABEL: Record<string, string> = {
  ad: 'AD',
  sb: 'SB',
  llp: 'LLP',
  qec: 'QEC',
  ldnd: 'LDND',
  blade: 'Blade',
};

const fallbackLabel = (reason: string | undefined): FallbackLabel | undefined =>
  reason === undefined
    ? undefined
    : ((FALLBACK_LABEL as Partial<Record<string, FallbackLabel>>)[reason] ?? UNKNOWN_FALLBACK);

const armName = (id: string): string => ARM_LABEL[id] ?? id;

const armsAgree = (arms: HtmlExtractionArm[]): boolean => {
  const first = arms[0];
  return arms.every(
    (arm) => arm.source === first.source && arm.verdict === first.verdict && arm.fallbackReason === first.fallbackReason,
  );
};

function armDisplay(arm: HtmlExtractionArm): ArmDisplay {
  const fallback = fallbackLabel(arm.fallbackReason);
  if (arm.source === 'sheet-html') return { text: 'Sheet HTML', degraded: false, title: 'Sheet HTML' };
  if (arm.source === 'passthrough') return { text: 'Source file', degraded: false, title: 'Source file' };
  if (arm.source === 'pdf' && fallback) {
    return {
      text: arm.verdict ? `${arm.verdict} — ${fallback.badge}` : fallback.badge,
      degraded: true,
      title: fallback.title,
    };
  }
  if (arm.source === 'pdf' && arm.verdict) {
    return { text: `${arm.verdict} — PDF used`, degraded: true, title: 'PDF used' };
  }
  if (arm.source === 'pdf') return { text: 'PDF used', degraded: true, title: 'PDF used' };
  if (fallback) {
    return {
      text: arm.verdict ? `${arm.verdict} — ${fallback.badge}` : fallback.badge,
      degraded: true,
      title: fallback.title,
    };
  }
  const degraded = arm.verdict === 'DEGRADED';
  return {
    text: arm.verdict ?? 'HTML',
    degraded,
    title: degraded ? 'HTML conversion degraded' : 'HTML conversion',
  };
}

function stampDisplay(htmlConversion: NonNullable<DocumentPageSchema['processing']>['htmlConversion']): ArmDisplay {
  const fallback = fallbackLabel(htmlConversion?.fallbackReason);
  const degraded = htmlConversion?.verdict === 'DEGRADED';
  return {
    text: fallback ? `${htmlConversion?.verdict} — ${fallback.badge}` : (htmlConversion?.verdict ?? ''),
    degraded: degraded || Boolean(fallback),
    title: fallback?.title ?? (degraded ? 'HTML conversion degraded' : 'HTML conversion'),
  };
}

// HTML conversion — stamped when extraction converted the page PDF to an HTML
// rendition (DR-2379). Arms (DR-3078) say what each leg actually read. DEGRADED
// HTML is still fed to extraction unless a `fallbackReason` is stamped.
export function HtmlConversionRow({ page }: { page: DocumentPageSchema }) {
  const [showHtml, setShowHtml] = useState(false);
  const [confirmReconvert, setConfirmReconvert] = useState(false);
  const [reconverting, setReconverting] = useState(false);
  const queryClient = useQueryClient();
  const htmlConversion = page.processing?.htmlConversion;
  const arms = page.processing?.dataExtraction?.result?.htmlArms;
  const armRows = arms && arms.length > 0 ? arms : undefined;
  if (!armRows && !htmlConversion) return null;

  const sharedArm = armRows && armsAgree(armRows) ? armDisplay(armRows[0]) : undefined;
  const rows = sharedArm
    ? [{ key: 'shared', ...sharedArm }]
    : armRows
      ? armRows.map((arm) => {
          const display = armDisplay(arm);
          return { key: arm.arm, ...display, text: `${armName(arm.arm)} — ${display.text}` };
        })
      : [{ key: 'stamp', ...stampDisplay(htmlConversion) }];
  const token = typeof htmlConversion?.token === 'string' ? htmlConversion.token : '';
  const showView = armRows ? token.length > 0 : true;
  const showReconvert =
    !armRows || armRows.some((arm) => arm.source === 'converted-html' || arm.source === 'pdf');

  const runReconvert = async () => {
    try {
      setReconverting(true);
      const r = await backend.pageRetry(page.id, 'data-extraction', { bypassHtmlCache: true });
      queryClient.setQueryData(['documentPage', page.id], r.data);
      await queryClient.removeQueries({ queryKey: ['documentPageHtml', page.id] });
    } catch (err) {
      console.error('html reconvert failed', err);
    } finally {
      setReconverting(false);
      setConfirmReconvert(false);
    }
  };

  return (
    <div>
      {rows.map((row, index) => (
        <dt
          key={row.key}
          className="text-sm font-medium text-gray-500 dark:text-zinc-400 flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2 sm:justify-between"
        >
          <span className="flex items-center gap-2 min-w-0">
            <StatusIndicator status={row.degraded ? 'degraded' : 'done'} title={row.title} />
            <span className="flex items-baseline gap-1.5 min-w-0 flex-wrap">
              <span>HTML conversion</span>
              <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400 shrink-0">{row.text}</span>
            </span>
          </span>
          {index === 0 && (showReconvert || showView) ? (
            <div className="flex items-center gap-2 shrink-0">
              {showReconvert ? (
                <AuthGuard roles={['admin', 'curator']}>
                  <ActionableButton buttonClassName="sm:py-0.5" plain onClick={() => setConfirmReconvert(true)}>
                    Reconvert HTML
                  </ActionableButton>
                </AuthGuard>
              ) : null}
              {showView ? (
                <ActionableButton buttonClassName="sm:py-0.5" plain onClick={() => setShowHtml(true)}>
                  View HTML
                </ActionableButton>
              ) : null}
            </div>
          ) : null}
        </dt>
      ))}
      {showView ? (
        <HtmlConversionDialog pageId={page.id} token={token} open={showHtml} onClose={() => setShowHtml(false)} />
      ) : null}
      {showReconvert ? (
        <ConfirmDialog
          open={confirmReconvert}
          title="Reconvert HTML"
          message="This deletes the cached HTML and runs extraction again."
          confirmLabel="Reconvert"
          pendingLabel="Reconverting…"
          color="amber"
          isPending={reconverting}
          onConfirm={() => void runReconvert()}
          onClose={() => setConfirmReconvert(false)}
        />
      ) : null}
    </div>
  );
}
